/* eslint-disable max-len */
import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { AbstractControl, FormArray, FormBuilder, FormControl, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import { STEPPER_GLOBAL_OPTIONS } from '@angular/cdk/stepper';
import { Store } from '@ngrx/store';
import { Observable, Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { AppState } from 'src/app/store/app.reducer';
import * as DeviceActions from 'src/app/store/device/device.actions';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { StandardDesignParameters, StandardDynamicComponent, StandardNewValidator, StandardPageLayout } from 'src/app/modules/shared/stdlayoutlib/classes.stdlayoutlib';
import { TranslateService } from '@ngx-translate/core';
import { CustomValidators } from 'src/app/validators/custom.validators';
import moment from 'moment';
import { selectDeviceSpecialDayModify } from 'src/app/store/device/device.selector';
import { SPECIAL_DAY_MODALITY } from 'src/app/conf/timers';
import { DeviceSpecialDays } from 'src/app/model/device-timer';

@Component({
	selector: 'app-device-special-day-dialog',
	templateUrl: './device-special-day-dialog.component.html',
	providers: [{
		provide: STEPPER_GLOBAL_OPTIONS, useValue: { showError: true }
	}]
})

export class DeviceSpecialDayDialogComponent implements OnInit, OnDestroy {
	specialDayModify$: Observable<boolean | undefined>;
	specialDayModifySub: Subscription | undefined;

	specialDayForm: FormGroup;
	specialDay: DeviceSpecialDays;

	listModality = SPECIAL_DAY_MODALITY;

	adding = true;

	minDate: Date = new Date();
	dateSel: Date = new Date();

	saving = false;

	// design
	screenWidth: number;
	MAX_MD_WIDTH = StandardDesignParameters.MAX_MD_WIDTH;

	constructor(
		private fb: FormBuilder,
		private store: Store<AppState>,
		private ref: DynamicDialogRef,
		public config: DynamicDialogConfig,
		private translate: TranslateService,
	) {
		if (config.data !== undefined && config.data.specialDay !== undefined) {
			this.specialDay = config.data.specialDay;
			this.adding = false;
		} else {
			this.specialDay = new DeviceSpecialDays();
		}
		this.initSelectors();
	}

	initSelectors(): void {
		this.specialDayModify$ = this.store.select(selectDeviceSpecialDayModify);
	}

	ngOnInit(): void {
		this.subscribeAll();
		this.buildForm();

		this.onResize();
		StandardNewValidator.setFirstCompile(true);
	}

	subscribeAll() {
		this.specialDayModifySub = this.specialDayModify$.pipe(filter(timerModify => !!timerModify))
			.subscribe(() => {
				this.resetClose();
			});
	}

	buildForm() {
		this.specialDayForm = new FormGroup({
			'startTime': new FormControl(this.getTime(this.specialDay.StartHour, this.specialDay.StartMin), Validators.required),
			'endTime': new FormControl(this.getTime(this.specialDay.EndHour, this.specialDay.EndMin), Validators.required),
			'modality': new FormControl(this.specialDay.Mode, Validators.required),
			'date': new FormControl(this.dateSel, Validators.required),
		});
		this.specialDayForm.addValidators(CustomValidators.checkCorrectTimeSequence('startTime', 'endTime'));
	}

	ngOnDestroy(): void {
		this.specialDayModifySub?.unsubscribe();
	}

	resetClose(): void {
		this.store.dispatch(DeviceActions.resetDeviceTimerForm());
		this.closeDialog();
	}

	submit(): void {
		StandardNewValidator.setFirstCompile(false);
		if (!this.getFormValidationErrors()) {
			this.save();
		}
	}

	getFormValidationErrors(): boolean {
		let atLeastOneError = false;
		Object.keys(this.specialDayForm.controls).forEach(key => {
			const controlErrors: ValidationErrors = this.specialDayForm.get(key).errors;
			if (controlErrors != null) {
				/* Object.keys(controlErrors).forEach(keyError => {
					console.log('Key control: ' + key + ', keyError: ' + keyError + ', err value: ', controlErrors[keyError]);
				}); */
				atLeastOneError = true;
			}
		});
		return atLeastOneError;
	}

	save(): void {
		this.saving = true;
		const day = moment(this.f.date.value);
		// START
		if (typeof this.f.startTime.value === 'string') {
			this.specialDay.StartHour = Number(this.f.startTime.value.split(':')[0]);
			this.specialDay.StartMin = Number(this.f.startTime.value.split(':')[1]);
		} else {
			this.specialDay.StartHour = moment(this.f.startTime.value).hours();
			this.specialDay.StartMin = moment(this.f.startTime.value).minutes();
		}
		// END
		if (typeof this.f.endTime.value === 'string') {
			this.specialDay.EndHour = Number(this.f.endTime.value.split(':')[0]);
			this.specialDay.EndMin = Number(this.f.endTime.value.split(':')[1]);
		} else {
			this.specialDay.EndHour = moment(this.f.endTime.value).hours();
			this.specialDay.EndMin = moment(this.f.endTime.value).minutes();
		}
		this.specialDay = {
			...this.specialDay,
			Mode: this.f.modality.value,
			Day: day.date(),
			Month: day.month() + 1,
			Year: day.year() - 2000,
		};
		if (this.adding) {
			this.store.dispatch(DeviceActions.addDeviceSpecialDay({ specialDay: this.specialDay }));
		} else {
			this.store.dispatch(DeviceActions.editDeviceSpecialDay({ specialDay: this.specialDay }));
		}
	}

	parseDays(): AbstractControl[] {
		const WDControl = this.f.weekDays as FormArray;
		const days = [];
		WDControl.controls.forEach((c, i) => {
			if (c.value) {
				days.push(i);
			}
		});
		return days;
	}

	getTime(hour: number, minute: number): string {
		return moment().hour(hour).minute(minute).format('HH:mm');
	}

	get f(): any {
		return this.specialDayForm.controls;
	}

	closeDialog(): void {
		this.ref.close();
	}

	// Design
	@HostListener('window:resize', ['$event'])
	onResize() {
		this.screenWidth = window.innerWidth;
	}

	onExtendedMobile(): boolean {
		return StandardPageLayout.onExtendedMobile(this.screenWidth);
	}

	col12Responsive(): string {
		return StandardPageLayout.col6Responsive(this.screenWidth);
	}

	dynamicButtonWidth(): string {
		return StandardPageLayout.dynamicButtonWidth(this.screenWidth);
	}

	dynamicInputTextWidth(): string {
		return StandardPageLayout.dynamicInputTextWidth(this.screenWidth, 'w-27rem');
	}

	justifyContentStyle(): string {
		return StandardNewValidator.justifyMessagesNewValidator(this.screenWidth);
	}

	newShowErrors(type: DeviceSpecialDayDialogComponent["f"], classType: string): string {
		return StandardNewValidator.newShowErrors(type, classType, this.screenWidth);
	}

	showErrorRequiredMessage(type: DeviceSpecialDayDialogComponent["f"]): boolean {
		return StandardNewValidator.showErrorRequiredMessage(type);
	}

	simpleInputTextLayout(classType: string): string {
		return StandardPageLayout.simpleInputTextLayout(classType, this.screenWidth);
	}

	dynamicDatepicker(): string {
		return StandardDynamicComponent.dynamicDatepicker(this.screenWidth);
	}

	customPaddingX(): string {
		return StandardPageLayout.customPaddingX(this.screenWidth);
	}

	dynamicInputShortTextWidth(): string {
		return StandardPageLayout.dynamicInputTextDesktopTabletWidth(this.screenWidth, 'w-20rem', 'w-27rem');
	}
}
